/* eslint-disable @typescript-eslint/no-explicit-any */
import eyeIcon from '../../../assets/svg/eye.svg?react';
import Datatable from '@/components/common/Datatable';

import {
  Box,
  Flex,
  Icon,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalHeader,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { convertDateFormat } from './../../../utils/dateConverter';
import StatusBadge from '@/components/common/StatusBadge';
import { GroupOrder } from '@/services/OrderManagement/types';
import { Column } from 'react-table';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useGetAllPaymentHistory } from '@/services/OrderManagement';
import GroupOrderDetails from '../GroupOrderDetails';
import { TAX_RATE } from '@/utils/constants';

interface FilterState {
  start_date?: string;
  end_date?: string;
  payment_method?: string;
  status?: boolean | null;
}

interface AllOrderProps {
  filters?: FilterState;
  setFilters?: React.Dispatch<React.SetStateAction<FilterState>>;
}

const AllOrder = ({ filters: parentFilters, setFilters: setParentFilters }: AllOrderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState<string>('');

  const [localFilters, setLocalFilters] = useState<FilterState>(
    parentFilters || {
      start_date: '',
      end_date: '',
      payment_method: '',
      status: null,
    },
  );

  useEffect(() => {
    if (parentFilters) {
      setLocalFilters(parentFilters);
    }
  }, [parentFilters]);

  const effectiveFilters = parentFilters ?? localFilters;

  const handleSetFilters = (newFilters: FilterState | ((prev: FilterState) => FilterState)) => {
    setLocalFilters(newFilters);
    if (setParentFilters) {
      setParentFilters(newFilters);
    }
  };

  const { data, isLoading, isError } = useGetAllPaymentHistory({
    page: pagination.page,
    page_size: pagination.pageSize,
    search: query || undefined,
    start_date: effectiveFilters.start_date || undefined,
    end_date: effectiveFilters.end_date || undefined,
    payment_method: effectiveFilters.payment_method || undefined,
    status: effectiveFilters.status,
  });

  useEffect(() => {
    if (data) {
      setPagination((prev) => ({
        ...prev,
        pageSize: data.pageSize || 10,
        totalCount: data.count || 0,
        totalPages: data.page_count || 0,
      }));
    }
  }, [data]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({
      ...prev,
      page: 1,
      totalCount: 0,
      totalPages: 0,
    }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    const resetState = {
      start_date: '',
      end_date: '',
      payment_method: '',
      status: null,
    };
    setLocalFilters(resetState);
    if (setParentFilters) {
      setParentFilters(resetState);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const flattenedData = useMemo(() => {
    if (!data?.results) return [];

    const orders: (GroupOrder & { itemsCount: number })[] = [];

    data.results.forEach((payment: any) => {
      const productItems = (payment.purchased_items || []).filter(
        (item: any) => item.cart_item !== null,
      );

      const addOnOnlyItems = payment.add_on_items || [];

      const allLineItems = [...productItems, ...addOnOnlyItems];

      if (allLineItems.length === 0) return;

      const shipment_address = payment.shipment_address || {};
      const addressParts = [
        shipment_address.address_line_1,
        shipment_address.address_line_2,
        shipment_address.town_city,
        shipment_address.landmark,
        shipment_address.pincode,
        shipment_address.state_detail?.name,
        shipment_address.country_detail?.country_name,
      ]
        .filter(Boolean)
        .map((p: string) => p.trim());

      const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '-';

      const statuses = productItems.map((item: any) => {
        const tracking = item.order_tracking || {};
        const statusName = tracking.order_status?.status_name || 'Processing';

        return {
          statusName,
          isUnderMaintenance: statusName === 'Under Maintenance',
          isReadyForDelivery: statusName === 'Ready for Delivery',
          isShipped: statusName === 'Shipped',
          isInWarehouse: tracking.is_in_warehouse === true,
          isOutForDelivery: statusName === 'Out for Delivery',
          isDelivered: tracking.has_delivered === true,
          isDeliveryFailed: tracking.has_delivery_failed === true,
          isCancelled: tracking.has_cancelled === true,
        };
      });

      let displayStatus = 'Not Shipped Yet';

      if (productItems.length === 0) {
        displayStatus = 'Add-on Purchase';
      } else if (statuses.some((s: any) => s.isUnderMaintenance)) {
        displayStatus = 'Under Maintenance';
      } else if (statuses.every((s: any) => s.isReadyForDelivery)) {
        displayStatus = 'Ready for Delivery';
      } else if (statuses.every((s: any) => s.isDelivered || s.isDeliveryFailed || s.isCancelled)) {
        // ... same logic
      } else if (statuses.some((s: any) => s.isShipped || s.isInWarehouse || s.isOutForDelivery)) {
        displayStatus = 'Delivery In Progress';
      }

      const itemsSummary = allLineItems.reduce(
        (acc: any, item: any) => {
          const cart = item.cart_item;
          const addOn = item.add_on;

          const qty = cart?.quantity || item.quantity || 1;
          let unitPrice = parseFloat(cart?.product_price || addOn?.price || '0');
          let addOnPrice = parseFloat(cart?.add_on_price || addOn?.price || '0');
          const customizedPrice = parseFloat(cart?.customized_price || '0');

          if (!cart && addOn) {
            unitPrice = 0;
            addOnPrice = parseFloat(item.price || '0');
          }

          acc.productTotal += unitPrice * qty;
          acc.customizedTotal += customizedPrice * qty;
          acc.addOnTotal += addOnPrice * qty;

          return acc;
        },
        { productTotal: 0, customizedTotal: 0, addOnTotal: 0 },
      );

      const totalQuantity = allLineItems.reduce(
        (sum: number, item: any) => sum + (item.cart_item?.quantity || item.quantity || 0),
        0,
      );

      const baseSubtotal =
        itemsSummary.productTotal + itemsSummary.customizedTotal + itemsSummary.addOnTotal;
      const estimatedTax = baseSubtotal * TAX_RATE;
      const calculatedTotal = parseFloat((baseSubtotal + estimatedTax).toFixed(2));

      orders.push({
        id: payment.id,
        bill_id: payment.bill_id,
        order_id: payment.bill_id || '-',
        shipping_address: {
          name: shipment_address.name || '-',
          address_line_1: shipment_address.address_line_1,
          address_line_2: shipment_address.address_line_2,
          town_city: shipment_address.town_city,
          landmark: shipment_address.landmark,
          pincode: shipment_address.pincode,
          state: shipment_address.state_detail?.name,
          country: shipment_address.country_detail?.country_name,
          location: undefined,
          full_address: fullAddress,
        },
        order_status: { status_name: displayStatus },
        total_price: calculatedTotal,
        currency: payment.currency || 'USD',
        payment: payment.payment_method?.method_name || '-',
        created_at: payment.created_at || '-',
        is_customized: productItems.some((i: any) => i.cart_item?.is_customized),
        transaction_id: payment.transaction_id,
        itemsCount: totalQuantity,
      } as GroupOrder & { itemsCount: number });
    });

    return orders;
  }, [data]);

  const columns = useMemo<Column<GroupOrder>[]>(
    () => [
      {
        Header: 'Bill ID',
        id: 'bill_id',
        accessor: (row) => row.bill_id || '-',

        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const billId = row.original.bill_id || '-';
          return (
            <Flex align="center" gap={2}>
              <Text>{billId}</Text>
            </Flex>
          );
        },
      },
      {
        Header: 'Quantity',
        id: 'quantity',
        accessor: 'itemsCount',
        Cell: ({ value }: { value: number }) => (
          <Text fontWeight="semibold" color="text.primary">
            {value} {value === 1 ? 'item' : 'items'}
          </Text>
        ),
      },
      {
        Header: 'Ship To',
        accessor: (row) => row.shipping_address?.name ?? '-',
        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: 'Status',
        accessor: (row) => row.order_status?.status_name ?? '-',
        Cell: ({ value }: { value: string }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Payment',
        accessor: 'payment',
        Cell: ({ value }: { value: string }) => <Text>{value ?? '-'}</Text>,
      },
      {
        Header: 'Total Price',
        id: 'total_price',
        accessor: (row) => row.total_price,
        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const price = row.original.total_price ?? 0;
          const currency = row.original.currency ?? 'USD';
          const symbol = getCurrencySymbol(currency);

          return (
            <Text fontWeight="semibold" color="text.primary">
              {symbol}
              {price.toFixed(2)}
            </Text>
          );
        },
      },
      {
        Header: 'Order Placed',
        accessor: 'created_at',
        Cell: ({ value }: { value: string }) => <Text>{convertDateFormat(value)}</Text>,
      },
      {
        Header: 'Action',
        id: 'action',
        Cell: ({ row }: { row: { original: GroupOrder } }) => {
          const orderId = row.original.id;

          return (
            <Flex gap={2} align="center" minWidth="80px">
              <Tooltip label="View Group Order" placement="bottom" hasArrow>
                <Box display="inline-block">
                  <Icon
                    as={eyeIcon}
                    boxSize={5}
                    color="brand.primary.DEFAULT"
                    onClick={() => {
                      handleViewOrder(String(orderId));
                    }}
                    cursor="pointer"
                  />
                </Box>
              </Tooltip>
            </Flex>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box bg="background.white">
      <Box width="auto" overflow="auto">
        <Datatable<GroupOrder>
          data={flattenedData}
          isLoading={isLoading}
          isError={isError}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalCount: pagination.totalCount,
            totalPages: pagination.totalPages,
          }}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          filters={effectiveFilters}
          setFilters={handleSetFilters}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          hideCategoryFilter={true}
          sticky={true}
        />
      </Box>

      <Modal size="5xl" isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Order Details</ModalHeader>
          <ModalBody
            maxHeight="70vh"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
            }}
          >
            {selectedOrderId ? (
              <>
                <GroupOrderDetails id={selectedOrderId} />
              </>
            ) : (
              <Text p={6}>No order selected...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AllOrder;
